import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RPE_RANGE } from "../../constants";
import {
  invalidRpeText,
  parseRpeInput,
  readInvalidRpeText,
  rpeValue,
} from "../../utils/rpe-range";

type Props = {
  id?: string;
  name?: string;
  /** Stored form text in RPE units: "", "8", "7-8" or "AMRAP". */
  value?: string | number | null;
  isRiR?: boolean;
  placeholder?: string;
  tabIndex?: number;
  className?: string;
  /**
   * Surfaces that save on every change set this false: an unreadable entry stays
   * local and is never saved, so the saved set is never invalid.
   */
  emitInvalid?: boolean;
  onChange: (event: { target: { name: string; value: string } }) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

/** Stored text ("7-8" in RPE units) → what the coach sees (RiR flipped when needed). */
export const rpeStoredToDisplay = (
  value: string | number | null | undefined,
  isRiR: boolean
) => {
  const text = String(value ?? "").trim();
  const invalid = readInvalidRpeText(text);
  if (invalid) return invalid.typed;
  if (!text || text.toUpperCase() === "AMRAP") return text.toUpperCase();
  const [first, second] = text.split("-");
  return rpeValue(second ?? first, second === undefined ? null : first, isRiR);
};

// RPE_RANGE starts at 4 and runs to 9, so +1 gives 5-10 in 0.5 steps.
const options: number[] = RPE_RANGE.map((step: number) => step + 1);

/**
 * Type-or-pick RPE: type 7 or 7-8, or pick a whole value or AMRAP from the list.
 * Emits stored text in RPE units; an unreadable entry is emitted as invalidRpeText
 * so the form can't be submitted until it is fixed.
 */
export const RpeInput = ({
  id,
  name = "rpe",
  value,
  isRiR = false,
  placeholder,
  tabIndex,
  className,
  emitInvalid = true,
  onChange,
  onKeyDown,
}: Props) => {
  const { t } = useTranslation("common");
  const listId = useId();
  const [text, setText] = useState(() => rpeStoredToDisplay(value, isRiR));
  const [error, setError] = useState<string | undefined>();
  // Errors are validated silently while typing ("7-" is just unfinished) and
  // only shown once the coach leaves the field or presses Enter.
  const [touched, setTouched] = useState(false);
  const [emitted, setEmitted] = useState<string>(String(value ?? ""));
  const emittedRef = useRef(emitted);
  emittedRef.current = emitted;

  // The parent resets or loads a value (set added, set opened for edit). Only a
  // changed prop counts: a surface that keeps passing the saved value while the
  // coach types must not wipe the text.
  useEffect(() => {
    const incoming = String(value ?? "");
    if (incoming !== emittedRef.current) {
      setEmitted(incoming);
      setText(rpeStoredToDisplay(incoming, isRiR));
      setError(undefined);
      setTouched(false);
    }
  }, [value, isRiR]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setText(next);
    setTouched(false);
    const parsed = parseRpeInput(next, isRiR);
    let stored: string;
    let isInvalid = false;
    if (parsed.error !== undefined) {
      setError(parsed.error);
      stored = invalidRpeText(parsed.error, next);
      isInvalid = true;
    } else {
      setError(undefined);
      if (parsed.rpe === null) stored = "";
      else if (parsed.rpeLower !== null)
        stored = `${parsed.rpeLower}-${parsed.rpe}`;
      else stored = String(parsed.rpe);
    }
    if (isInvalid && !emitInvalid) return;
    setEmitted(stored);
    onChange({ target: { name, value: stored } });
  };

  const showError = Boolean(error) && touched;

  return (
    <span className="relative block">
      <input
        id={id}
        type="text"
        autoComplete="off"
        list={listId}
        value={text}
        placeholder={placeholder}
        tabIndex={tabIndex}
        className={className}
        aria-invalid={showError ? true : undefined}
        data-has-error={showError ? "true" : undefined}
        data-rpe-error={error ? t(error) : undefined}
        onChange={handleChange}
        onBlur={() => {
          setTouched(true);
          // tidy a valid entry: "amrap" -> "AMRAP", "7 - 8" -> "7-8"
          if (!error) setText(rpeStoredToDisplay(emitted, isRiR));
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") setTouched(true);
          onKeyDown?.(event);
        }}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={rpeStoredToDisplay(option, isRiR)} />
        ))}
        <option value="AMRAP" />
      </datalist>
      <input
        type="hidden"
        name={name}
        value={readInvalidRpeText(emitted) ? "" : emitted}
      />
      {showError ? (
        <p
          role="alert"
          className="absolute top-full left-0 z-10 mt-1 w-max max-w-64 rounded-md bg-(--dropdown-background-color) px-2 py-1 text-xs text-(--danger) shadow-lg"
        >
          {t(error)}
        </p>
      ) : null}
    </span>
  );
};

export default RpeInput;
